<script>
import { mapActions } from 'vuex';
import DeleteWarning from './delete-warning.vue';
import i18n from "@/plugins/i18n";

export default {
    components: {
        DeleteWarning,
    },

    props: {
        selectedCompanyId: {
            type: Number,
            required: true
        },

        userId: {
            type: Number,
            required: true
        },

        years: {
            type: Object,
            required: true
        },
    },

    data() {
        return {
            defaultNetworks: null,

            items: null,
            fallbackValue: null,

            itemToAdd: {
                company_development_network_id: null,
                company_development_network_name: '',
                company_development_network_membership_date: null,
            },
            addingItem: false,

            deletingItem: null,
        }
    },

    computed: {
        headers() {
            return [
                { key: 'delete', title: '', flex: 1 },
                { key: 'date', title: i18n.t('companyProfile.date'), flex: 3 },
                { key: 'name', title: i18n.t('companyProfile.name'), flex: 8 },
            ]
        },

        filteredDefaults() {
            if (!this.defaultNetworks) return [];
            if (this.defaultNetworks.find(x => x.development_network_name === this.itemToAdd.company_development_network_name)) {
                return [];
            }

            if (this.itemToAdd.company_development_network_name.length >= 2) {
                return this.defaultNetworks.filter(x => x.development_network_name.toLowerCase().includes(this.itemToAdd.company_development_network_name.toLowerCase()));
            } else {
                return [];
            }
        }
    },

    mounted() {
        this._getDefaultNetworks();
        this.getItems();
    },

    methods: {
        ...mapActions('company', [
            'getDefaultNetworks',
            'getDevNetworks',
            'createDevNetworks',
            'updateDevNetwork',
            'deleteDevNetwork',
        ]),

        _getDefaultNetworks() {
            this.getDefaultNetworks({ is_deleted: false })
                .then(res => {
                    this.defaultNetworks = res.data;
                })
        },

        getItems() {
            const params = {
                company_id: this.selectedCompanyId,
                is_deleted: false
            }

            this.getDevNetworks(params)
                .then(res => {
                    this.items = res.data;
                    if (res.data.length === 0) {
                        this.setAddingItem(true);
                    }
                })
        },

        createItem() {
            if (this.itemToAdd.company_development_network_membership_date == null ||
                this.itemToAdd.company_development_network_name == '' ||
                this.filteredDefaults.length > 0) {
                /* this.$notify('error', 'Please make sure to have filled all the necessary information.', true); */
                return;
            }

            const params = {
                company_id: this.selectedCompanyId,
                created_by: this.userId,
                company_development_network_membership_date: this.itemToAdd.company_development_network_membership_date,
                company_development_network_name: this.itemToAdd.company_development_network_name
            }

            this.createDevNetworks(params)
                .then(res => {
                    this.itemToAdd.company_development_network_id = res.data.company_development_network_id;
                    this.items.push(this.itemToAdd);

                    this.sortItems();

                    this.handleResetAddingItem();

                    this.setAddingItem(false);

                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })

        },

        updateItem(e, key, id) {
            if (!e.target.value || e.target.value.length === 0) return;

            const params = {
                company_development_network_id: id,
                body: {
                    [`${key}`]: e.target.value
                }
            }

            this.updateDevNetwork(params)
                .then(res => {
                    this.setFallbackValue(null);
                    this.sortItems();
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.items.find(x => x.company_development_network_id == id)[`${key}`] == this.fallbackValue;
                    this.setFallbackValue(null);
                    this.$notify('error', response.data.messageName);
                })
        },

        deleteItem() {
            this.deleteDevNetwork({ company_development_network_id: this.deletingItem.company_development_network_id })
                .then(() => {
                    this.items = this.items.filter(x => x.company_development_network_id != this.deletingItem.company_development_network_id);
                    this.setDeletingItem(null);
                    this.$notify('success', '', true);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        /* handlers */

        handleResetAddingItem() {
            this.itemToAdd = {
                company_development_network_id: null,
                company_development_network_name: '',
                company_development_network_membership_date: null,
            };

            this.setAddingItem(false);
        },

        sortItems() {
            this.items.sort((a, b) => b.company_development_network_membership_date - a.company_development_network_membership_date);
        },

        handleDefaultNetworkTraverse(e) {
            if (this.filteredDefaults.length == 0) return;

            const focusedElement = document.activeElement;
            if (!focusedElement) return;

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                const previousElement = focusedElement.previousElementSibling;
                if (previousElement && previousElement.classList.contains('default-network-item')) {
                    previousElement.focus();
                }
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                const nextElement = focusedElement.nextElementSibling;
                if (nextElement && nextElement.classList.contains('default-network-item')) {
                    nextElement.focus();
                }
            } else if (e.key === 'Enter') {
                this.handleSelectDefaultNetwork(focusedElement.textContent.trim());
            }
        },

        handleSelectDefaultNetwork(name) {
            this.itemToAdd.company_development_network_name = name;

            if (this.itemToAdd.company_development_network_membership_date) {
                this.createItem();
            }
        },

        /* setters */

        setFallbackValue(val) {
            this.fallbackValue = val;
        },

        setAddingItem(bool) {
            this.addingItem = bool;
        },

        setDeletingItem(item) {
            this.deletingItem = item;
        },

    }
}
</script>

<template>
    <div class="items">
        <div>
            {{ $t('companyProfile.modal8.text') }}
        </div>

        <div class="item-headers">
            <div v-for="header in headers" :key="header.key" :style="{ flex: header.flex }">
                {{ header.title }}
            </div>
        </div>

        <div class="item" v-for="(item, i) in items" :key="i">
            <div class="delete-item-button">
                <button @click="setDeletingItem(item)">
                    <i class="fa-regular fa-trash-can"></i>
                </button>

                <delete-warning v-if="deletingItem == item" @onClose="setDeletingItem(null)" @onOk="deleteItem()" />
            </div>

            <select v-model="item.company_development_network_membership_date" :style="{ flex: headers[1].flex }"
                @change="updateItem($event, 'company_development_network_membership_date', item.company_development_network_id)">
                <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                </option>
            </select>

            <input type="text" :style="{ flex: headers[2].flex }" v-model="item.company_development_network_name"
                maxlength="255"
                @change="updateItem($event, 'company_development_network_name', item.company_development_network_id)"
                @focus="setFallbackValue(item.company_development_network_name)" @blur="setFallbackValue(null)" />
        </div>

        <div class="item" v-if="addingItem">
            <div class="delete-item-button">
                <button @click="handleResetAddingItem()" @keyup.enter="handleResetAddingItem()">
                    <i class="fa-regular fa-square-minus"></i>
                </button>
            </div>

            <select v-model="itemToAdd.company_development_network_membership_date" :style="{ flex: headers[1].flex }"
                @change="createItem()">
                <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                </option>
            </select>

            <input type="text" :style="{ flex: headers[2].flex }" v-model="itemToAdd.company_development_network_name"
                maxlength="255" @change="createItem()"
                @focus="setFallbackValue(itemToAdd.company_development_network_name)" @blur="setFallbackValue(null)" />

            <div v-if="filteredDefaults.length > 0" class="default-development-networks">
                <div v-for="default_network in filteredDefaults" :key="default_network.development_network_id"
                    tabindex="0" class="default-network-item" @keydown="handleDefaultNetworkTraverse"
                    @click="handleSelectDefaultNetwork(default_network.development_network_name)">
                    {{ default_network.development_network_name }}
                </div>
            </div>
        </div>

        <div class="add-item-ctr" v-if="items.length === 0 || !addingItem">
            <button @click="setAddingItem(true)" @keyup.enter="setAddingItem(true)" :disabled="addingItem">
                +
            </button>
        </div>
    </div>
</template>

<style lang='scss' scoped>
.items {
    padding-top: .5em;
    min-height: 100px !important;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
}

.item-headers {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    >div {
        text-align: left;
        margin: .5em;
        color: var(--sustainfinity-navy);
        font-weight: bold;
        font-size: 1.1em;
    }
}

.item {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    align-content: flex-start;
    flex-wrap: wrap;

    >.delete-item-button {
        margin: .5em;
        flex: 1;
        display: flex;
        align-items: center;

        >.cannot-edit {
            font-size: .8em;
            font-style: italic;
            color: var(--invalid-input);
        }

        >button {
            display: flex;
            align-items: center;

            outline: 0;
            border: 0;

            background-color: transparent !important;

            &:focus {
                outline: thin solid lightgray !important;
            }

            >i {
                color: var(--invalid-input);
            }

            &:hover {
                >i {
                    color: var(--invalid-input--hover);
                }
            }

            &:disabled {
                pointer-events: none !important;

                >i {
                    color: gray;
                }
            }
        }

        position: relative;

    }

    >input,
    select {
        text-align: left;
        margin: .5em;
        outline: 0;
        border: 0;
        border-bottom: thin solid lightgray;
        background-color: whitesmoke;

        transition: border-bottom 500ms;

        &:focus {
            border-bottom: thin solid black;
        }

        &:disabled {
            border-radius: .2em;
            background-color: rgb(221, 221, 221);
            cursor: not-allowed;
        }
    }

    position: relative;

    >.default-development-networks {
        z-index: 100;
        position: absolute;
        bottom: 0;
        right: 0;
        transform: translateY(100%);

        box-shadow: 0 .25em .5em .25em gray;
        outline: thin solid lightgray;
        border-radius: .25em;

        overflow-y: auto;
        max-height: 150px;
        width: 66%;

        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-end;

        >div {
            background-color: var(--sustainfinity-faded-blue);
            width: 100%;

            padding: .5em;
            border-bottom: thin solid lightgray;

            &:last-of-type {
                border-bottom: 0 !important;
            }

            cursor: pointer;

            &:hover {
                background-color: var(--sustainfinity-faded-blue-darker);
            }

            &:focus {
                background-color: var(--sustainfinity-faded-blue-even-darker);
                outline: 0;
                border: 0;
                border-bottom: thin solid lightgray;
            }
        }
    }
}

.add-item-ctr {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 1em 0;

    >button {
        width: 50px;
        height: 30px;
        font-size: 1.2em;
        font-weight: bold;
        color: white;
        background-color: var(--sustainfinity-yellow);
        border-radius: .25em;

        outline: 0;
        border: 0;

        &:hover {
            background-color: var(--sustainfinity-yellow--hover);
        }

        &:focus {
            outline: thin solid gray !important;
        }
    }
}
</style>